<template>
    <popup
      ref="popup"
      :button-style="duplicate ? 'info' : buttonStyle"
      :heading="drawerHeading"
      :button-text="label || (duplicate ? duplicateLabel : (rowData ? '' : buttonText))"
      :plain="duplicate && !!duplicateLabel"
      center-button
      :disable-button="disableButton || !isManager('finance|management|admin')"
      :button-icon="duplicate ? 'fa fa-clone' : (rowData ? 'fa fa-edit' : buttonIcon)"
      :button-aria-label="duplicate ? 'Duplicate rate' : (rowData ? 'Edit rate' : null)"
      :button-title="duplicate ? 'Duplicate rate' : (rowData ? 'Edit rate' : null)"
      :show-save="!publishOnly"
      :show-save-publish="(allowPublish || publishOnly) && view === 'sharer'"
      @save="onSave"
      @save-publish="onSavePublish"
      @close="onClose">

      <el-form v-if="showRange && view !== 'sharer'" label-position="top">

          <el-row :gutter="12">

              <el-col :sm="24" :md="24":lg="18" align="center">
                <el-form-item label="Tenor Range (months)">
                  <el-slider
                     v-model="months"
                     range
                     show-stops
                     :step="6"
                     :min="0"
                     :max="maxMonths"
                     :marks="marks"
                      @change="updateMonths"
                      :disabled="disabled">
                   </el-slider>
                 </el-form-item>
              </el-col>

              <el-col :sm="24" :md="24" :lg="6" align="center">
                <el-form-item :label="plural('Year', years)">
                  <timeline :months="months" :max-months="maxMonths" :periods="periods" />
                </el-form-item>
              </el-col>

          </el-row>

      </el-form>

      <component :is="comp" :key="rowData ? rowData.id : 'new'" ref="rateDetails" :row-data="row" />

    </popup>

</template>



<script>

import axios from '@/lib/axiosClient'
import SiteMixins from '@/mixins/SiteMixins'

import SharerRateDetails from './SharerRateDetails'
import TenancyRateDetails from './TenancyRateDetails'
import UtilityRateDetails from './UtilityRateDetails'

import Timeline from './Timeline'

export default {

  mixins: [SiteMixins],

  components: {
    Timeline
  },

  props: {

    view: {
      type: String,
      default: 'sharer'
    },

    rowData: {
      type: Object,
      default: null
    },

    showRange: {
      type: Boolean,
      default: true
    },

    tableRef: {
      type: String,
      default () { return this.view + 'ratesttable' }
    },

    duplicate: {
      type: Boolean,
      default: false
    },

    duplicateLabel: {
      type: String,
      default: ''
    },

    disableButton: {
      type: Boolean,
      default: false
    },

    allowPublish: {
      type: Boolean,
      default: false
    },

    publishOnly: {
      type: Boolean,
      default: false
    },

    label: {
      type: String,
      default: ''
    }

  },

  methods: {

    init () {

      switch (this.view) {
        case 'sharer':
              this.buttonText = 'Sharer'
              this.buttonIcon = 'fa fa-users'
              this.buttonStyle = 'warning'
              this.rateGroupPrefix = 80000
              this.comp = SharerRateDetails
              break

        case 'tenancy':
              this.buttonText = 'Tenancy'
              this.buttonIcon = 'fa fa-home'
              this.buttonStyle = 'success'
              this.rateGroupPrefix = 30000
              this.comp = TenancyRateDetails
              break

        case 'utility':
              this.buttonText = 'Utility'
              this.buttonIcon = 'fa fa-plug'
              this.buttonStyle = 'primary'
              this.rateGroupPrefix = 50000
              this.comp = UtilityRateDetails
              break

      }

      if (this.duplicate && this.rowData) {
        // Duplicate mode: deep-clone the source row, strip id + tier ids,
        // keep seqNo, append " (copy)" to name. row.id === null → onSave POSTs.
        var cloned = this.clone(this.rowData)
        delete cloned.id
        cloned.name = (cloned.name || '') + ' (copy)'
        if (cloned.tiers && Array.isArray(cloned.tiers)) {
          cloned.tiers = cloned.tiers.map(function (t) {
            var tier = Object.assign({}, t)
            delete tier.id
            return tier
          })
        }
        this.row = cloned
      } else if (!this.row) {
        this.row = this.getTableStruct('rates')
        this.row.status = 'Active'
        this.row.groupId = this.rateGroupPrefix
        this.row.type = this.buttonText
      }

      if (this.row && (this.row.fromMonth || this.row.toMonth)) {
        this.months = []
        this.months.push(this.row.fromMonth)
        this.months.push(this.row.toMonth)
      }

    },

    updateMonths(months) {

      if (this.isArray(months)) {

          this.months = months
          this.row.fromMonth = months[0]
          this.row.toMonth = months[1]
        }

    },

    prepareRow () {
      // For sharer view: validate tiers before saving.
      // Returns true if ready to save, false if validation failed (message already shown).
      if (this.view === 'sharer' && this.$refs.rateDetails) {
        var result = this.$refs.rateDetails.validateTiers()
        if (!result.valid) {
          // Propagate per-tier errors into the child for display
          this.$refs.rateDetails.setTierErrors(result.errors)
          this.$message.error('Fix the highlighted tier errors before saving.')
          return false
        }
        // Clear errors on valid
        this.$refs.rateDetails.setTierErrors([])

        // Serialize tiers: coerce numbers
        var tiers = this.row.tiers
        if (tiers && tiers.length > 0) {
          this.row.tiers = tiers.map(function (t) {
            var out = {
              seqNo: Number(t.seqNo),
              fromMonth: Number(t.fromMonth),
              toMonth: Number(t.toMonth),
              telco1: Number(t.telco1) || 0,
              telco2: Number(t.telco2) || 0,
              telco3: Number(t.telco3) || 0,
              telco4: Number(t.telco4) || 0,
              telco5: Number(t.telco5) || 0,
              telco6: Number(t.telco6) || 0,
              telco7: Number(t.telco7) || 0,
              telco8: Number(t.telco8) || 0,
              telco9: Number(t.telco9) || 0,
              telco10: Number(t.telco10) || 0,
              mwRate: Number(t.mwRate) || 0,
              rruRate: Number(t.rruRate) || 0,
              rruSet: Number(t.rruSet) || 0
            }
            return out
          })

          // Mirror tier-1 onto flat columns for backend transition read-compat
          var tier1 = this.row.tiers[0]
          this.row.fromMonth = tier1.fromMonth
          this.row.toMonth = tier1.toMonth
          this.row.telco1 = tier1.telco1
          this.row.telco2 = tier1.telco2
          this.row.telco3 = tier1.telco3
          this.row.telco4 = tier1.telco4
          this.row.telco5 = tier1.telco5
          this.row.telco6 = tier1.telco6
          this.row.telco7 = tier1.telco7
          this.row.telco8 = tier1.telco8
          this.row.telco9 = tier1.telco9
          this.row.telco10 = tier1.telco10
          this.row.mwRate = tier1.mwRate
          this.row.rruRate = tier1.rruRate
          this.row.rruSet = tier1.rruSet
        }
      }
      return true
    },

    onSave () {
      if (!this.prepareRow()) return

      var self = this
      this.saveForm(this, '/rates', this.row.id, this.row, this.buttonText, function (response) {
        self.$emit('saved', response)
      }, null, true, this.tableRef)
    },

    onSavePublish () {
      var self = this
      this.$confirm(
        'Publish creates a new immutable version of this rate and makes it the current version. Licensees following the current rate will recompute to these tiers in the background; licensees pinned to an earlier version keep theirs. Continue?',
        'Save + Publish',
        {
          confirmButtonText: 'Save + Publish',
          cancelButtonText: 'Cancel',
          type: 'warning',
          center: true
        }
      ).then(function () {
        if (!self.prepareRow()) return
        self.saveForm(self, '/rates', self.row.id, self.row, self.buttonText, function (rate) {
          var id = rate && (rate.id || (rate.data && rate.data.id))
          self.send(
            self,
            self.getEndpoint('/rates/' + id + '/publish'),
            {},
            'rate version',
            function (resp) {
              self.$emit('saved', rate)
              self.$emit('published', resp)
              if (self.$refs.popup) self.$refs.popup.closeModal()
            },
            null,
            axios.post,
            true,
            'Publish',
            self.tableRef,
            true
          )
        // null tableRef on the intermediate save — the publish send below fires the
        // single table refresh, so the resolved row drops once (not twice).
        }, null, true, null)
      }).catch(function () {})
    },

    onClose () {
//      this.$message('Closing...')
    }

  },

  computed: {

    years: function () {
      return this.formatNumber((this.months[1] - this.months[0]) / 12, 1)
    },

    drawerHeading: function () {
      if (this.duplicate) {
        return 'Duplicate ' + this.titleCase(this.view) + ' Rate'
      }
      return this.buttonText
    }

  },

  watch: {

    // vuetable recycles this component instance across pagination/sort, updating
    // the rowData prop in place on the SAME instance. Without re-syncing, the
    // data().row snapshot stays the first rate ever rendered — so after paging,
    // clicking Edit on a row opened the WRONG rate. Re-sync the working copy
    // here; the :key on the detail <component> re-mounts the form so it
    // re-normalizes its tiers from the new row.
    // Duplicate mode: re-run init() so the cloned seed is rebuilt from the new rowData.
    rowData (val) {
      if (this.duplicate) {
        this.init()
      } else {
        this.row = val || this.getTableStruct('rates')
        if (this.row && (this.row.fromMonth || this.row.toMonth)) {
          this.months = [this.row.fromMonth, this.row.toMonth]
        }
      }
    }

  },



  data () {
    return {
      row: this.duplicate ? null : this.rowData,
      disabled: (this.rowData && !this.duplicate) ? true : false,
      data: [],

      comp: null,
      buttonText: null,
      buttonIcon: null,
      buttonStyle: null,
      heading: null,
      rateGroupPrefix: null,

      months: [0, 36],
      maxMonths: 300,
      periods: 3,

      marks: {
        24: '2 yrs',
        72: '6 yrs',
        120: '10 yrs',
        168: '14 yrs',
        216: '18 yrs',
        264: '22 yrs'
      }

    }
  },

  created () {
    this.init()
  }

}
</script>
